import { jsx, jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { anchorDisplayLabel, stableAnchorKey } from "../utils/contractAnnotations";
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";

export function ContractAnnotationsSidebar({
  annotations = [],
  selectedAnchorKey = "",
  onSelectAnchor,
  onReply,
  onResolve,
  canReply = false,
  canResolve = false,
  title = "Comments",
  emptyHint = "No comments yet.",
  /** When true, stretch to fill a flex parent (e.g. gap report sidebar) instead of a fixed max-height. */
  fillAvailableHeight = false,
}) {
  const [replyDrafts, setReplyDrafts] = useState({});
  const openList = annotations.filter((a) => a.status !== "resolved");
  const resolvedList = annotations.filter((a) => a.status === "resolved");

  const setDraft = (id, text) => setReplyDrafts((d) => ({ ...d, [id]: text }));

  const rootClass = fillAvailableHeight
    ? "flex min-h-0 h-full max-h-full w-full flex-1 flex-col overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm"
    : "flex flex-col border border-[color:var(--color-border)] rounded-2xl bg-[color:var(--color-bg-white)] shadow-sm overflow-hidden shrink-0 w-full lg:w-[300px] xl:w-[320px] max-h-[min(72vh,640px)]";

  return /* @__PURE__ */ jsxs("div", {
    className: rootClass,
    children: [
      /* @__PURE__ */ jsx("div", {
        className: "px-4 py-3 border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-hero)] shrink-0",
        children: /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest", children: title }),
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "min-h-0 flex-1 overflow-y-auto p-3 space-y-3",
        children: [
          openList.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)] font-medium px-1", children: emptyHint }),
          openList.map((a) => {
            const ak = stableAnchorKey(a.anchor);
            const focused = selectedAnchorKey === ak;
            const quote =
              a.anchor?.type === "textQuote" && a.anchor.quote
                ? String(a.anchor.quote).slice(0, 280) + (String(a.anchor.quote).length > 280 ? "\u2026" : "")
                : null;
            return /* @__PURE__ */ jsxs(
              "div",
              {
                className: `rounded-xl border p-3 text-left transition-all ${focused ? "border-[color:var(--color-accent-fill)] bg-[color:var(--color-info-bg)] ring-1 ring-[color:var(--color-info-line)]" : "border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)]"}`,
                children: [
                  /* @__PURE__ */ jsx("button", {
                    type: "button",
                    onClick: () => onSelectAnchor && onSelectAnchor(ak),
                    className: "w-full text-left",
                    children: /* @__PURE__ */ jsx("p", {
                      className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)] mb-1",
                      children: anchorDisplayLabel(a.anchor),
                    }),
                  }),
                  quote &&
                    /* @__PURE__ */ jsx("p", {
                      className: "text-[11px] text-[color:var(--color-text-muted)] bg-[color:var(--color-warn-bg)] border border-[color:var(--color-warn-line)] rounded-lg px-2 py-1.5 mb-2 whitespace-pre-wrap",
                      children: quote,
                    }),
                  a.kind === "suggestion" &&
                    /* @__PURE__ */ jsx("span", {
                      className: "inline-block text-[9px] font-black uppercase text-[color:var(--color-accent)] mb-1",
                      children: "Suggestion",
                    }),
                  a.kind === "reaction" &&
                    /* @__PURE__ */ jsx("span", {
                      className: "inline-block text-[9px] font-black uppercase text-[color:var(--color-warn-fg)] mb-1",
                      children: "Reaction",
                    }),
                  /* @__PURE__ */ jsx("p", {
                    className: `whitespace-pre-wrap font-medium ${a.kind === "reaction" ? "text-2xl leading-none" : "text-xs text-[color:var(--color-text-hero)]"}`,
                    children: a.body || "-",
                  }),
                  /* @__PURE__ */ jsxs("p", {
                    className: "text-[10px] text-[color:var(--color-text-muted)] mt-1",
                    children: [a.authorRole, " \u00B7 ", formatDdMmYyyyTime(a.createdAt)],
                  }),
                  Array.isArray(a.reactions) &&
                    a.reactions.length > 0 &&
                    /* @__PURE__ */ jsx("div", {
                      className: "flex flex-wrap gap-1 mt-1",
                      children: a.reactions.map((r, i) =>
                        /* @__PURE__ */ jsx("span", { className: "text-sm bg-[color:var(--color-bg-white)] border border-[color:var(--color-border)] rounded-full px-1.5", title: r.authorRole, children: r.emoji }, i)
                      ),
                    }),
                  (Array.isArray(a.replies) ? a.replies : []).map((r, i) =>
                    /* @__PURE__ */ jsxs("div", { className: "mt-2 pl-2 border-l-2 border-[color:var(--color-border)]", children: [
                      /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted-dark)] whitespace-pre-wrap", children: r.body }),
                      /* @__PURE__ */ jsxs("p", {
                        className: "text-[10px] text-[color:var(--color-text-muted)]",
                        children: [r.authorRole, " \u00B7 ", formatDdMmYyyyTime(r.createdAt)],
                      }),
                    ]}, i)
                  ),
                  canReply &&
                    onReply &&
                    /* @__PURE__ */ jsxs("div", {
                      className: "mt-2 space-y-1",
                      children: [
                        /* @__PURE__ */ jsx("textarea", {
                          value: replyDrafts[a.id] || "",
                          onChange: (e) => setDraft(a.id, e.target.value),
                          placeholder: "Reply\u2026",
                          rows: 2,
                          className: "w-full text-xs border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]",
                        }),
                        /* @__PURE__ */ jsx("button", {
                          type: "button",
                          onClick: () => {
                            const t = (replyDrafts[a.id] || "").trim();
                            if (!t) return;
                            onReply(a.id, t);
                            setDraft(a.id, "");
                          },
                          className: "px-3 py-1 bg-[color:var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:opacity-90",
                          children: "Reply",
                        }),
                      ],
                    }),
                  canResolve &&
                    onResolve &&
                    /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => onResolve(a.id),
                      className: "mt-2 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)] border border-[color:var(--color-ok-line)] rounded-lg hover:bg-[color:var(--color-ok-bg)]",
                      children: "Resolve",
                    }),
                ],
              },
              a.id
            );
          }),
          resolvedList.length > 0 &&
            /* @__PURE__ */ jsxs("div", {
              className: "pt-2 border-t border-[color:var(--color-border)]",
              children: [
                /* @__PURE__ */ jsx("p", {
                  className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] px-1 mb-2",
                  children: "Resolved",
                }),
                resolvedList.map((a) =>
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: "rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-alt)] p-2 mb-2 opacity-75",
                      children: [
                        /* @__PURE__ */ jsx("p", { className: "text-[9px] font-black uppercase text-[color:var(--color-text-muted)] mb-0.5", children: anchorDisplayLabel(a.anchor) }),
                        /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[color:var(--color-text-muted)] line-clamp-2", children: a.body }),
                      ],
                    },
                    `r-${a.id}`
                  )
                ),
              ],
            }),
        ],
      }),
    ],
  });
}
